import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { create } from "zustand";

import { api, type MeasureChange } from "../../api/client";
import type { Deployment } from "../../api/types";
import { t, tc, tServer } from "../../i18n/i18n";
import { type Notice, useUi } from "../../state/ui";
import { type DossierWrite, dossierWrite, recordWrite, useDossierHistory, writeFailure } from "../dossierWrites";
import { history, useHistoryState } from "../history";
import { serverNotes } from "../orderSync";
import { type HistoryStep, runHistoryStep } from "./useManoeuvreGestures";
import { useDossierLocked } from "../../sharing/access";

/** Écritures des lignes de la ZO (docs/15 §3.7, §4.3 à §4.5), une seule implémentation pour les trois origines :
 *  glissement ou inclinaison sur la carte (`useMeasureGestures`), fiche de la ligne (`MeasureCard`) et panneau
 *  Manœuvre. Position choisie d'une ligne (`PUT /measures`, `ends`), position doctrinale (`ends: null`), retrait
 *  et rétablissement de la CFL ou de la FSCL (`off`), retour de toutes les lignes aux positions calculées
 *  (`DELETE /measures`). Comme les autres écritures de la carte, elles passent par la portée du dossier et
 *  s'inscrivent dans le canal « dispositif » de l'historique, sous le libellé donné : ⌘Z, ou « Annuler » de l'avis,
 *  les défait. L'OPO n'est pas recomposé (les lignes de la ZO n'y figurent pas).
 *
 *  Un seul état « occupé » (`measureWritesBusy`) : glissement en cours sur la carte, écriture en vol de n'importe
 *  quelle origine, annulation différée (⌘Z pendant une écriture en vol). La fiche et le panneau n'écrivent pas
 *  pendant ce temps (la position de départ d'un geste ou d'une action serait déjà périmée) ; la carte ne saisit pas
 *  une ligne pendant une écriture de la fiche ou du panneau (`cardPending`) ni pendant une annulation différée
 *  (`undoPending` : l'écriture du nouveau glissement retirerait de l'historique l'entrée à défaire, ou serait
 *  effacée par l'annulation), mais enchaîne ses propres glissements (aperçu de l'écriture en vol, B28). Un seul libellé d'échec
 *  (« Ligne de la ZO non enregistrée ») et un seul avis de conflit (`writeFailure`), quelle que soit l'origine. */

/** Avis du serveur que l'avis de l'écriture redit déjà (`zo_measures.REMOVED_NOTES`, `RESET_NOTE`) : écartés du
 *  détail. */
export const MEASURE_NOTICES = ["CFL retirée", "FSCL retirée", "Lignes et zones de la ZO replacées selon le dispositif"];

export interface MeasureWrite {
  /** Modification envoyée (`PUT /measures`) ; null : toutes les lignes aux positions calculées (`DELETE /measures`). */
  change: MeasureChange | null;
  /** Action inscrite dans l'historique, en minuscule et déjà traduite (« déplacement de FLOT (T2) »). */
  label: string;
  /** Texte de l'avis, déjà traduit (« CFL (GTIA) retirée ») ; absent (glissement de la carte) : avis seulement si
   *  le serveur signale un conflit nouveau (« La FLOT dépasse la CFL en T2 »). */
  text?: string;
  /** Écriture d'un glissement de la carte (le geste garde son aperçu jusqu'à la réponse) ; sinon fiche ou panneau. */
  gesture?: boolean;
}

interface MeasureWriteState {
  /** Ligne de la ZO saisie sur la carte (entre `grab` et `release` / `abort`). */
  dragging: boolean;
  /** Écritures en vol (portée du dossier comprise) des glissements de la carte, et de la fiche ou du panneau. */
  mapPending: number;
  cardPending: number;
  /** Annulations demandées pendant une écriture en vol, pas encore remises à l'historique (`deferHistoryStep`). */
  undoPending: number;
}

export const useMeasureWriteState = create<MeasureWriteState>()(() => ({
  dragging: false,
  mapPending: 0,
  cardPending: 0,
  undoPending: 0,
}));

/** Glissement de ligne commencé ou fini (`useMeasureGestures`). */
export function setMeasureDragging(dragging: boolean): void {
  if (useMeasureWriteState.getState().dragging !== dragging) useMeasureWriteState.setState({ dragging });
}

/** Glissement en cours, écriture de ligne de la ZO en vol (quelle que soit son origine) ou annulation différée. */
export function measureWritesBusy(state: MeasureWriteState = useMeasureWriteState.getState()): boolean {
  return state.dragging || state.mapPending > 0 || state.cardPending > 0 || state.undoPending > 0;
}

/** La carte peut saisir une ligne : ni écriture de la fiche ou du panneau en vol, ni annulation différée. */
export function measureGrabAllowed(state: MeasureWriteState = useMeasureWriteState.getState()): boolean {
  return state.cardPending === 0 && state.undoPending === 0;
}

/** Dernière écriture partie, en attente de réponse : vrai si elle est enregistrée. */
let inflight: Promise<boolean> | null = null;

/** Garde des annulations (⌘Z, ↶ ↷, posée par `useMeasureGestures`) pendant une écriture de ligne en vol, quelle
 *  que soit son origine : l'annulation attend la réponse puis défait cette écriture (sinon elle défairait l'action
 *  d'avant, que l'écriture écraserait ensuite) ; un rétablissement est ignoré. Vrai si l'étape est prise en charge.
 *  Jusqu'à sa remise à l'historique, l'annulation compte comme « occupée » (`undoPending`). */
export function deferHistoryStep(step: HistoryStep): boolean {
  const pending = inflight;
  if (!pending) return false;
  if (step !== "undo") return true;
  useMeasureWriteState.setState((s) => ({ undoPending: s.undoPending + 1 }));
  void pending.then((saved) => {
    useMeasureWriteState.setState((s) => ({ undoPending: Math.max(0, s.undoPending - 1) }));
    if (saved) history.undo();
  });
  return true;
}

/** Détail d'un avis : les autres avis du serveur, sans ceux que le texte de l'avis redit. */
function otherNotes(result: Deployment): string | undefined {
  return serverNotes({ ...result, notices: (result.notices ?? []).filter((n) => !MEASURE_NOTICES.includes(n)) }) || undefined;
}

/** Conflits signalés par le serveur après l'écriture et absents avant (déjà traduits), joints ; vide sinon. */
function newWarnings(write: DossierWrite): string {
  const before = new Set(write.previous.corridor?.measures?.warnings ?? []);
  return (write.result.corridor?.measures?.warnings ?? [])
    .filter((w) => !before.has(w))
    .map((w) => tServer(w))
    .join(" · ");
}

/** Avis après une écriture enregistrée, avec « Annuler » (par la garde des gestes : un glissement en cours est
 *  abandonné plutôt que l'action défaite). */
function notifyWrite(write: DossierWrite, input: MeasureWrite, recorded: boolean): void {
  const fresh = newWarnings(write);
  if (!input.text && !fresh) return;
  const notice: Omit<Notice, "id"> = {
    text: input.text ?? t("Ligne de la ZO déplacée"),
    detail: [fresh, otherNotes(write.result)].filter(Boolean).join(" · ") || undefined,
    tone: fresh ? "warn" : input.change === null ? "info" : "ok",
    action: recorded ? { label: tc("défaire", "Annuler"), run: () => runHistoryStep("undo") } : undefined,
  };
  useUi.getState().notify(notice);
}

/** Écriture d'une ligne de la ZO (ou de toutes, `change: null`). Fiche ou panneau pendant un glissement ou une autre
 *  écriture de ligne : rien n'est envoyé ni désigné (leurs boutons sont alors inactifs), null. Échec : avis
 *  (conflit : historique remis à zéro, dossier rechargé) puis rejet, pour que le geste ramène sa ligne. */
export async function writeMeasure(queryClient: QueryClient, input: MeasureWrite): Promise<DossierWrite | null> {
  if (!input.gesture && measureWritesBusy()) return null;
  const counter = input.gesture ? "mapPending" : "cardPending";
  useMeasureWriteState.setState((s) => ({ [counter]: s[counter] + 1 }));
  const { change } = input;
  const promise = dossierWrite(
    queryClient,
    (target, opo) => (change ? api.setMeasure(target, change, opo) : api.resetMeasures(target, opo)),
    { notSaved: t("Enregistrez le dossier pour déplacer les lignes de la ZO."), channel: "dispositif", orders: false },
  );
  const saved = promise.then(
    () => true,
    () => false,
  );
  inflight = saved;
  void saved.then(() => {
    if (inflight === saved) inflight = null;
    useMeasureWriteState.setState((s) => ({ [counter]: Math.max(0, s[counter] - 1) }));
  });
  try {
    const write = await promise;
    notifyWrite(write, input, recordWrite(write, input.label));
    return write;
  } catch (error) {
    writeFailure(queryClient, error, change ? t("Ligne de la ZO non enregistrée") : t("Lignes de la ZO non enregistrées"));
    throw error;
  }
}

/** Écritures de la fiche et du panneau ; `busy` couvre aussi les glissements de la carte et l'historique. */
export function useMeasureWrites() {
  // Dossier en lecture seule pour le compte (docs/17 §3.2) : aucune écriture, l'appui désigne le bandeau.
  const locked = useDossierLocked();
  const queryClient = useQueryClient();
  const params = useUi((s) => s.deploymentParams);
  const historyBusy = useHistoryState((s) => s.busy);
  const busy = useMeasureWriteState(measureWritesBusy);
  useDossierHistory();
  const run = (input: MeasureWrite) => void writeMeasure(queryClient, input).catch(() => undefined);
  return {
    /** Dossier enregistré, brouillon non modifié : les écritures sont possibles (sinon `ui.requestSave`). */
    canEdit: Boolean(params?.exercise_id) && !locked,
    setMeasure: { mutate: (input: Omit<MeasureWrite, "gesture">) => run(input) },
    resetMeasures: {
      mutate: () =>
        run({
          change: null,
          label: t("retour des lignes de la ZO aux positions doctrinales"),
          text: t("Lignes et zones de la ZO replacées selon le dispositif"),
        }),
    },
    /** Écriture en cours, glissements de la carte, annulations / rétablissements de l'historique compris. */
    busy: historyBusy || busy,
  };
}
